import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MiceImportPreview } from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { miceRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useOrgContext, useSnackbar } from '@web/ui/core';
import { downloadBlob, fireAndForget } from '@web/ui/core/utils';
import { errorMessage } from './use_mice_orders';

/**
 * Importação de viajantes num ponto só: escolher o arquivo pede a prévia (formato reconhecido e
 * contagens, nada gravado); confirmar grava pelo token. Os modelos e o export ficam aqui também.
 */
export function useMiceImport(projectId: string) {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const fileInput = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<MiceImportPreview | null>(null);
  const onError = (err: unknown): void => {
    showSnackbar({ severity: 'error', message: errorMessage(err) });
  };

  const analyze = useMutation({
    mutationFn: (file: File) => miceRepository.previewImport(orgId, projectId, file),
    onSuccess: setPreview,
    onError,
    meta: { silent: true },
  });
  const confirm = useMutation({
    mutationFn: (token: string) => miceRepository.confirmImport(orgId, projectId, token),
    onSuccess: async (result) => {
      setPreview(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.mice.project(orgId, projectId) });
      showSnackbar({
        severity: 'success',
        message: t('mice.upload.done', {
          inserted: result.inserted,
          invalid: result.invalid,
          duplicate: result.duplicate,
        }),
      });
    },
    onError,
    meta: { silent: true },
  });

  return {
    fileInput,
    preview,
    uploading: analyze.isPending || confirm.isPending,
    pick: () => fileInput.current?.click(),
    analyze: (file: File) => {
      analyze.mutate(file);
    },
    confirm: () => {
      if (preview) confirm.mutate(preview.token);
    },
    cancel: () => {
      setPreview(null);
    },
    downloadNeedsTemplate: () => {
      fireAndForget(
        miceRepository.downloadNeedsTemplate(orgId).then((b) => {
          downloadBlob(b, 'modelo-necessidades-mice.csv');
        }),
      );
    },
    exportResult: () => {
      fireAndForget(
        miceRepository.exportOrders(orgId, projectId).then((b) => {
          downloadBlob(b, `mice-${projectId}.csv`);
        }),
      );
    },
    downloadOrdersTemplate: () => {
      fireAndForget(
        miceRepository.downloadTemplate(orgId).then((b) => {
          downloadBlob(b, 'modelo-mice.csv');
        }),
      );
    },
  };
}

export type MiceImport = ReturnType<typeof useMiceImport>;
